/**
 * @component ry-kbd
 * @category  display
 * @summary   Inline keyboard-key label. Ported from daisyUI's `.kbd` component
 *            (MIT, https://daisyui.com/components/kbd) — translated 1:1 from
 *            their Tailwind-utility composition into vanilla CSS that consumes
 *            ry-ui tokens. Renders an `<kbd>` element with a key-cap chrome
 *            (small border, slightly darker bottom edge, rounded, monospace
 *            font) so consumers can typeset shortcuts inline (`<kbd>Ctrl</kbd>
 *            + <kbd>K</kbd>`). Wrap the content in a real `<kbd>` element so
 *            the semantic stays intact for screen readers; the host carries
 *            the sizing attribute.
 *
 * Reference: daisyUI v5.5.20 — packages/daisyui/src/components/kbd.css
 *            (vendored verbatim at demos/parity/vendor/daisy/kbd/component.css).
 *            License: MIT (Pouya Saadeghi).
 *
 * @attribute size  'xs' | 'sm' | 'md' | 'lg' | 'xl'  default='md'
 *            Pure CSS hook for the five daisyUI size variants. Controls box
 *            height/min-width and font-size. `md` matches the unsized default.
 *
 * @content   The kbd's textContent is lifted into an inner `<kbd>` element on
 *            setup so the semantic tag survives without the consumer needing
 *            to type `<ry-kbd><kbd>Ctrl</kbd></ry-kbd>`. If the consumer
 *            already provided a `<kbd>` child, ry-kbd uses it as-is.
 *
 * @dom
 *   ry-kbd[size?]
 *     > kbd.ry-kbd__key  [data-ry-target="key"]
 *         > {key text}
 *
 * @gotcha  ry-kbd renders ONE key per element. For a combination, compose
 *          multiple kbd elements with literal separators between them:
 *          `<ry-kbd>Ctrl</ry-kbd> + <ry-kbd>K</ry-kbd>`. This matches the
 *          HTML spec's intent for `<kbd>` (one keystroke per element).
 *
 * @gotcha  The size attribute is a pure CSS hook — there is no JS that reads
 *          it. Changing it at runtime restyles the element immediately.
 *
 * @example basic
 * ```html
 * <ry-kbd>Ctrl</ry-kbd>
 * ```
 *
 * @example combination
 * ```html
 * <span>
 *   <ry-kbd>Ctrl</ry-kbd> + <ry-kbd>Shift</ry-kbd> + <ry-kbd>P</ry-kbd>
 * </span>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-kbd size="xs">xs</ry-kbd>
 * <ry-kbd size="sm">sm</ry-kbd>
 * <ry-kbd size="md">md</ry-kbd>
 * <ry-kbd size="lg">lg</ry-kbd>
 * <ry-kbd size="xl">xl</ry-kbd>
 * ```
 *
 * @tutorial wraps-content-in-semantic-kbd
 * ```js
 * // Wrong assumption: the host element is the <kbd>. The host is the styling
 * // shell; the actual semantic <kbd> is rendered inside so screen readers and
 * // RSS-style tooling still see a real keyboard tag.
 * const el = mount('<ry-kbd>Ctrl</ry-kbd>');
 * const key = el.querySelector('[data-ry-target="key"]');
 * await expectEqual(key.tagName, 'KBD', 'inner element is a semantic <kbd>');
 * await expectEqual(key.textContent.trim(), 'Ctrl', 'key text is preserved');
 * ```
 *
 * @tutorial default-size-is-md
 * ```js
 * // Wrong assumption: missing size means no size class. ry-kbd treats absent
 * // size as the md default — but the attribute itself is NOT auto-set on the
 * // host. Read the rendered font-size or trust the default; do not query
 * // `el.getAttribute("size")` expecting "md".
 * const el = mount('<ry-kbd>A</ry-kbd>');
 * await expectEqual(el.getAttribute('size'), null, 'size attribute stays absent by default');
 * ```
 *
 * @tutorial size-attribute-survives-on-host
 * ```js
 * // Size is a CSS hook — JS does not move it, rename it, or strip it.
 * const el = mount('<ry-kbd size="lg">F1</ry-kbd>');
 * await expectAttr(el, 'size', 'lg');
 * ```
 *
 * @see ry-tag
 * @see ry-code
 */

import { RyElement } from '../core/ry-element.js';

export class RyKbd extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="key"]')) return;

    // If consumer already nested a <kbd>, decorate it in place. Otherwise lift
    // the textContent into a fresh <kbd> so the semantic tag is preserved.
    const existing = this.querySelector('kbd');
    if (existing) {
      existing.setAttribute('data-ry-target', 'key');
      existing.classList.add('ry-kbd__key');
      return;
    }

    const text = this.textContent ?? '';
    this.innerHTML = `<kbd data-ry-target="key" class="ry-kbd__key">${text}</kbd>`;
  }
}

customElements.define('ry-kbd', RyKbd);
